import {PermissionType} from '@hconnect/apiclient'
import {FLEX_PAGE_HEADER_MX, FLEX_PAGE_HEADER_MY} from '@hconnect/common/components'
import {PAGE_HEADER_ML} from '@hconnect/uikit/src/lib2/pageContainer/Page'
import {getPlantName, useUpmPlantsContext} from '@hconnect/upm'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import {
  Menu,
  MenuItem,
  Typography,
  Breadcrumbs as MUIBreadcrumbs,
  Box,
  SxProps,
  Theme
} from '@mui/material'
import {useMemo, useRef, useState} from 'react'
import {useParams} from 'react-router'

import {usePlantsByPermission} from '../../shared/hooks'
import {useReplacePlantId} from '../hooks'

type PathParameter = {
  plantId?: string
}

type PlantSelectBreadcrumbProps = {
  permission: PermissionType
  sx?: SxProps<Theme>
}

export const PlantSelectBreadcrumb = ({permission, sx}: PlantSelectBreadcrumbProps) => {
  const {plantId: currentPlantId} = useParams<PathParameter>()
  if (!currentPlantId) throw new Error('plantId is missing in the URL parameters')

  const upmPlants = useUpmPlantsContext()
  const plantName = getPlantName(upmPlants, currentPlantId)

  const [openDropdown, setOpenDropdown] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const replacePlantId = useReplacePlantId()

  const {plantIds} = usePlantsByPermission(permission)

  const handleToggle = () => setOpenDropdown((prevOpen) => !prevOpen)

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return
    }

    setOpenDropdown(false)
  }

  const handleMenuItemClick = (newPlantId: string) => {
    setOpenDropdown(false)
    replacePlantId(newPlantId)
  }

  const hasManyPlants = plantIds.length > 1

  const sortedOptions = useMemo(() => {
    const options = plantIds.map((id) => ({
      id,
      name: getPlantName(upmPlants, id)
    }))
    return options.sort((a, b) => (a.name > b.name ? 1 : -1))
  }, [plantIds, upmPlants])

  return (
    <Box
      component="span"
      display="flex"
      ml={FLEX_PAGE_HEADER_MX + PAGE_HEADER_ML}
      mt={FLEX_PAGE_HEADER_MY}
      sx={sx}
    >
      <MUIBreadcrumbs>
        {hasManyPlants ? (
          <>
            <Typography
              component="span"
              color="common.white"
              ref={anchorRef}
              onClick={handleToggle}
              sx={{cursor: 'pointer'}}
            >
              {plantName}
              <Box component="span" sx={{ml: 0.5}}>
                {openDropdown ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </Box>
            </Typography>
            <Menu
              anchorEl={anchorRef.current}
              open={openDropdown}
              onClose={handleClose}
              sx={{mt: 1}}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
            >
              {sortedOptions.map(({id, name}) => {
                return (
                  <MenuItem
                    key={id}
                    onClick={() => handleMenuItemClick(id)}
                    data-test-id={`menu-item-${id}`}
                    selected={id === currentPlantId}
                  >
                    {name}
                  </MenuItem>
                )
              })}
            </Menu>
          </>
        ) : (
          <Typography component="span" color="common.white">
            {plantName}
          </Typography>
        )}
      </MUIBreadcrumbs>
    </Box>
  )
}
